import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sparkles, Phone, Mail, MapPin, Search, Rocket, DollarSign, Gift, ImageIcon, Save, Zap, TrendingDown, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUploader from "@/components/ImageUploader";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Autonomos() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [me, setMe] = useState({ has_profile: false });
  const [plans, setPlans] = useState([]);
  const [profile, setProfile] = useState({
    profession: "", services: [], description: "", banner_url: "", picture: "",
    phone: "", contact_email: "", city: "", quote_mode: "free", quote_price_brl: 0,
  });
  const [servicesInput, setServicesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [l, m, p] = await Promise.all([
      axios.get(`${API}/autonomos`).catch(() => ({ data: [] })),
      axios.get(`${API}/autonomos/me`).catch(() => ({ data: { has_profile: false } })),
      axios.get(`${API}/autonomo-plans`).catch(() => ({ data: [] })),
    ]);
    setList(l.data);
    setMe(m.data);
    if (m.data?.profile) {
      setProfile({ ...profile, ...m.data.profile });
      setServicesInput((m.data.profile.services || []).join(", "));
    }
    setPlans(p.data);
    // eslint-disable-next-line
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const search = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    const r = await axios.get(`${API}/autonomos?${params.toString()}`);
    setList(r.data);
  };

  const saveProfile = async () => {
    if (!profile.profession || !profile.phone || !profile.contact_email) {
      return toast.error("Profissão, telefone e e-mail são obrigatórios");
    }
    setSaving(true);
    try {
      const services = servicesInput.split(",").map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/autonomos/me`, {
        ...profile,
        services,
        quote_price_brl: parseFloat(profile.quote_price_brl) || 0,
      });
      toast.success("Página salva!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const subscribe = async (plan) => {
    if (!me.has_profile) return toast.error("Crie sua página primeiro na aba 'Minha página'");
    if (!window.confirm(`Assinar ${plan.name} por R$ ${plan.final_price_brl.toFixed(2)}?`)) return;
    setBusy(plan.plan_id);
    try {
      await axios.post(`${API}/autonomos/subscribe`, { plan_id: plan.plan_id });
      toast.success(`Plano ${plan.name} ativo! Sua página está publicada.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <SponsoredAdWidget variant="inline" limit={2} />
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="autonomos-heading">Profissionais autônomos</h1>
        <p className="text-muted-foreground mt-1">Encontre um profissional ou publique sua própria página de divulgação.</p>
      </div>

      <Tabs defaultValue="browse">
        <TabsList className="rounded-full flex flex-wrap w-full max-w-3xl gap-1 h-auto p-1">
          <TabsTrigger value="browse" data-testid="auto-tab-browse" className="rounded-full text-xs">Buscar</TabsTrigger>
          <TabsTrigger value="mine" data-testid="auto-tab-mine" className="rounded-full text-xs">Minha página</TabsTrigger>
          <TabsTrigger value="plans" data-testid="auto-tab-plans" className="rounded-full text-xs">Planos</TabsTrigger>
        </TabsList>

        {/* BROWSE */}
        <TabsContent value="browse" className="mt-4 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="autonomos-search-q" placeholder="Buscar por profissão, serviço..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="pl-9" />
            </div>
            <Input data-testid="autonomos-search-city" placeholder="Cidade" value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="max-w-[200px]" />
            <Button data-testid="autonomos-search-btn" onClick={search} className="rounded-full">Buscar</Button>
          </div>

          {list.length === 0 && (
            <Card className="p-10 text-center text-muted-foreground">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Nenhum profissional publicado ainda. Que tal ser o primeiro?
            </Card>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(a => (
              <Card key={a.user_id} className="overflow-hidden" data-testid={`autonomo-card-${a.user_id}`}>
                <div className="h-32 relative bg-gradient-to-br from-primary/30 to-purple-500/30">
                  {a.banner_url && <img src={a.banner_url} alt="" className="w-full h-full object-cover" />}
                  {a.quote_mode === "paid" ? (
                    <Badge className="absolute top-2 right-2 bg-orange-500 gap-1"><DollarSign className="w-3 h-3" />Orçamento R$ {Number(a.quote_price_brl || 0).toFixed(2)}</Badge>
                  ) : (
                    <Badge className="absolute top-2 right-2 bg-green-500 gap-1"><Gift className="w-3 h-3" />Orçamento grátis</Badge>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={a.picture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${a.user_id}`} alt="" className="w-14 h-14 rounded-full -mt-10 border-2 border-background" />
                    <div className="flex-1">
                      <div className="font-bold">{a.name || a.profession}</div>
                      <div className="text-xs text-muted-foreground">{a.profession}</div>
                    </div>
                  </div>
                  {a.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-2">{a.description}</p>}
                  {a.services?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.services.slice(0, 4).map((s, i) => <Badge key={`${s}-${i}`} variant="secondary" className="text-[10px]">{s}</Badge>)}
                    </div>
                  )}
                  <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                    {a.city && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.city}</div>}
                    <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</div>
                    <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.contact_email}</div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a href={`tel:${a.phone}`} className="flex-1"><Button size="sm" className="w-full rounded-full">Ligar</Button></a>
                    <a href={`mailto:${a.contact_email}`} className="flex-1"><Button size="sm" variant="outline" className="w-full rounded-full">E-mail</Button></a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* MINE */}
        <TabsContent value="mine" className="mt-4 space-y-4">
          {me.active_subscription ? (
            <Card className="p-3 bg-green-500/10 border-green-500/40 text-sm" data-testid="auto-active-sub">
              ✓ Plano ativo: <strong>{me.active_subscription.plan_name}</strong> até {new Date(me.active_subscription.expires_at).toLocaleDateString()}
            </Card>
          ) : (
            me.has_profile && (
              <Card className="p-3 bg-orange-500/10 border-orange-500/40 text-sm" data-testid="auto-no-sub">
                ⚠ Sua página está criada mas <strong>não está publicada</strong>. Assine um plano na aba <Link to="#" className="underline">Planos</Link> para ficar visível.
              </Card>
            )
          )}

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight">Sua página de divulgação</h2>
                <p className="text-xs text-muted-foreground">Foto banner + telefone + e-mail + profissão + serviços.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs flex items-center gap-1"><ImageIcon className="w-3 h-3" />Banner (URL da foto de capa)</Label>
                <Input data-testid="auto-banner" placeholder="https://... (foto de capa)" value={profile.banner_url} onChange={(e) => setProfile({ ...profile, banner_url: e.target.value })} />
                <div className="mt-1">
                  <ImageUploader onUploaded={(url) => setProfile({ ...profile, banner_url: url })} label="Enviar banner" testid="auto-banner-upload" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Foto de perfil (URL)</Label>
                <Input data-testid="auto-picture" placeholder="https://..." value={profile.picture} onChange={(e) => setProfile({ ...profile, picture: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Profissão</Label>
                <Input data-testid="auto-profession" placeholder="Ex: Encanador, Advogado, Personal trainer" value={profile.profession} onChange={(e) => setProfile({ ...profile, profession: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Serviços oferecidos (vírgula)</Label>
                <Input data-testid="auto-services" placeholder="Ex: Instalação, Manutenção, Reparo" value={servicesInput} onChange={(e) => setServicesInput(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descrição / apresentação</Label>
                <Textarea data-testid="auto-description" rows={3} value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input data-testid="auto-phone" placeholder="(11) 9..." value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">E-mail</Label>
                <Input data-testid="auto-email" placeholder="email@..." value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Cidade</Label>
                <Input data-testid="auto-city" placeholder="Cidade" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} />
              </div>

              <div className="md:col-span-2 p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-2" data-testid="auto-quote-block">
                <div className="font-semibold text-sm">Modalidade de Orçamento</div>
                <div className="flex gap-2">
                  <button type="button" data-testid="auto-quote-free" onClick={() => setProfile({ ...profile, quote_mode: "free" })} className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-1 ${profile.quote_mode === "free" ? "border-green-500 bg-green-500/10" : "border-border"}`}>
                    <Gift className="w-4 h-4" /> Grátis
                  </button>
                  <button type="button" data-testid="auto-quote-paid" onClick={() => setProfile({ ...profile, quote_mode: "paid" })} className={`flex-1 p-2 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-1 ${profile.quote_mode === "paid" ? "border-orange-500 bg-orange-500/10" : "border-border"}`}>
                    <DollarSign className="w-4 h-4" /> Cobrado
                  </button>
                </div>
                {profile.quote_mode === "paid" && (
                  <div>
                    <Label className="text-xs">Valor do orçamento (R$) — pago diretamente a você</Label>
                    <Input data-testid="auto-quote-price" type="number" min="0" step="0.5" value={profile.quote_price_brl} onChange={(e) => setProfile({ ...profile, quote_price_brl: e.target.value })} />
                  </div>
                )}
              </div>
            </div>

            <Button data-testid="auto-save" onClick={saveProfile} disabled={saving} className="rounded-full gap-1 mt-4">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar página"}
            </Button>
          </Card>
        </TabsContent>

        {/* PLANS */}
        <TabsContent value="plans" className="mt-4">
          <Card className="p-4 bg-gradient-to-br from-primary/10 to-transparent border-primary/40 mb-4">
            <div className="flex items-center gap-3">
              <Rocket className="w-6 h-6 text-primary" />
              <div>
                <div className="font-bold">Publique sua página com um plano</div>
                <div className="text-xs text-muted-foreground">Desconto progressivo — anual é o mais vantajoso.</div>
              </div>
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.map(p => (
              <Card key={p.plan_id} className={`p-5 relative overflow-hidden ${p.discount_pct >= 40 ? "border-primary/50 bg-primary/5" : ""}`} data-testid={`auto-plan-${p.plan_id}`}>
                {p.discount_pct > 0 && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-orange-500 text-[10px] gap-1"><TrendingDown className="w-3 h-3" /> -{p.discount_pct.toFixed(0)}%</Badge>
                  </div>
                )}
                {p.discount_pct >= 40 && (
                  <div className="absolute top-2 left-2">
                    <Badge className="bg-primary text-[10px] gap-1"><Sparkles className="w-3 h-3" /> Melhor</Badge>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2 mt-4">
                  <Calendar className="w-4 h-4 text-primary" />
                  <div className="font-bold text-lg">{p.name}</div>
                </div>
                <div className="text-3xl font-bold text-primary">R$ {p.final_price_brl.toFixed(2)}</div>
                {p.discount_pct > 0 && <div className="text-xs text-muted-foreground line-through">de R$ {p.base_price_brl.toFixed(2)}</div>}
                <div className="text-xs text-muted-foreground">{p.days} dia(s)</div>
                <ul className="text-xs mt-3 space-y-1">
                  {(p.features || []).map((f, i) => <li key={`${f}-${i}`} className="flex items-center gap-1"><Zap className="w-3 h-3 text-primary" /> {f}</li>)}
                </ul>
                <Button data-testid={`auto-plan-sub-${p.plan_id}`} size="sm" onClick={() => subscribe(p)} disabled={busy === p.plan_id} className="w-full rounded-full mt-4">
                  {busy === p.plan_id ? "Processando..." : "Assinar"}
                </Button>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
